import { Box, Button, Typography } from '@mui/material';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import styles from './Location.module.scss';
import { memo, useEffect, useState } from 'react';
import locationApi from '~/apis/location';
import { cast, handleApiError, isWard, isXaphuongTinhThanh, notify } from '~/utils/helper';
import { AddNewIcon } from '~/assets/icons';
import { ILocation } from '~/utils/interface/location';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT, PAGE_INDEX } from '~/utils/constants/common';
import dayjs from 'dayjs';
import BaseRow from '~/components/common/base-row/BaseRow';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import EditLocationDialog from './components/EditLocationDialog';
import ConfirmDialog from '~/components/common/confirm-dialog/ConfirmDialog';
import { ImeiEnum, LocationTypeEnum } from '~/utils/enum';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';
import { locationActions } from '~/thunks/location/locationSlice';
import LocationList from './components/LocationList';
import { handleGetLocationList } from '~/thunks/location/locationThunk';
import DetailLocationDialog from './components/DetailLocationDialog';
import { IMasterDataItem } from '~/utils/interface/master';

const cx = classNames.bind(styles);

const Location = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/location');

  const { selectedUser, userProfile } = useAppSelector((state) => state.auth);
  const currentUser = Object.values(selectedUser).length === 0 ? userProfile : selectedUser;

  const { filter, needRefreshData } = useAppSelector((state) => state.location);

  const [isOpenEditLocationDialog, setIsOpenEditLocationDialog] = useState<boolean>(false);
  const [editLocationDialogMode, setEditLocationDialogMode] = useState<string>(DIALOG_MODE_CREATE);
  const [isOpenDeleteLocationDialog, setIsOpenDeleteLocationDialog] = useState<boolean>(false);
  const [isOpenDetailLocationDialog, setIsOpenDetailLocationDialog] = useState<boolean>(false);
  const [editData, setEditData] = useState<ILocation>();

  const [provinceOptions, setProvinceOptions] = useState<IMasterDataItem[]>([]);
  const [wardOptions, setWardOptions] = useState<IMasterDataItem[]>([]);

  const locationTypeOptions = [
    {
      id: LocationTypeEnum.VAO,
      name: t('location_type.' + LocationTypeEnum.VAO),
    },
    {
      id: LocationTypeEnum.RA,
      name: t('location_type.' + LocationTypeEnum.RA),
    },
    {
      id: LocationTypeEnum.VAO_RA,
      name: t('location_type.' + LocationTypeEnum.VAO_RA),
    },
  ];

  const statusOptions = [
    {
      id: ImeiEnum.UNASSIGNED,
      name: t('is_assigned.' + ImeiEnum.UNASSIGNED),
    },
    {
      id: ImeiEnum.ASSIGNED,
      name: t('is_assigned.' + ImeiEnum.ASSIGNED),
    },
  ];

  useEffect(() => {
    if (!currentUser?.username) return;

    dispatch(handleGetLocationList({ ...filter, username: currentUser?.username }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filter, needRefreshData, currentUser?.username]);

  useEffect(() => {
    getListProvinces();
  }, []);

  useEffect(() => {
    const provinceId = filter.provinceId || currentUser?.province_id;
    if (!provinceId) {
      setWardOptions([]);
      return;
    }

    (async () => {
      try {
        const response = await locationApi.getListWards({
          province: cast(provinceId),
        });
        setWardOptions(
          response.data.map((item) => {
            return {
              id: item.code,
              name: item.name,
              data0: item.name,
              data1: item.name_with_type,
              data2: item.type,
              data3: item.parent_code,
            };
          })
        );
      } catch (error) {
        setWardOptions([]);
        handleApiError(error);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.provinceId, currentUser?.province_id]);

  // Reset when change login
  useEffect(() => {
    return () => {
      setWardOptions([]);
      dispatch(locationActions.resetFilter());
    };
  }, [dispatch]);

  const getListProvinces = async () => {
    try {
      const response = await locationApi.getListProvinces();
      setProvinceOptions(
        response.data.map((item) => {
          return {
            id: item.code,
            name: item.name,
            data0: item.name,
            data1: item.name_with_type,
            data2: item.type,
          };
        })
      );
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleClickAddNew = () => {
    setIsOpenEditLocationDialog(true);
    setEditLocationDialogMode(DIALOG_MODE_CREATE);
  };

  const handleSetOpenEditDialog = (open: boolean, needRefreshData?: boolean) => {
    setIsOpenEditLocationDialog(open);

    if (needRefreshData) dispatch(locationActions.changeNeedRefreshData(true));
  };

  const handleDeleteLocation = async () => {
    try {
      if (!editData) return;

      await locationApi.delete(cast(editData.id));
      dispatch(locationActions.changeNeedRefreshData(true));
      setIsOpenDeleteLocationDialog(false);
      notify('success', t('deleteSuccess', { ns: 'admin/common', name: t('location') }));
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleOpenEdit = async (location: ILocation) => {
    try {
      if (location) setEditLocationDialogMode(DIALOG_MODE_EDIT);
      setEditData(location);
      setIsOpenEditLocationDialog(true);
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleOpenDelete = async (location: ILocation) => {
    setEditData(location);
    setIsOpenDeleteLocationDialog(true);
  };

  const handleOpenDetail = async (location: ILocation) => {
    setEditData(location);
    setIsOpenDetailLocationDialog(true);
  };

  const handleClearFilter = () => {
    dispatch(locationActions.resetFilter());
  };

  return (
    <Box className={cx('location-screen')}>
      <Box className='flex justify-between items-center bg-[#123B6D] h-20 px-6'>
        <Typography className='text-white font-semibold text-xl'>{t('list')}</Typography>
        <Button
          className='bg-white rounded-xl text-[#123B6D] h-12 hover:bg-white hover:opacity-80 text-base font-bold px-5'
          startIcon={<AddNewIcon />}
          onClick={handleClickAddNew}
        >
          {t('create')}
        </Button>
      </Box>

      <BaseRow className='gap-4 px-6 pt-6 mb-7 2xl:!items-end max-[1280px]:!flex-col'>
        <Box className='w-full'>
          <BaseRow className='gap-4 mb-4 max-[1280px]:!flex-col'>
            <Box className='min-[1280px]:w-1/3 w-full'>
              <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('province_id')}</Typography>
              <BaseSelect
                options={
                  isXaphuongTinhThanh(cast(currentUser?.roleid))
                    ? provinceOptions.filter((item: IMasterDataItem) => item.id === String(currentUser?.province_id))
                    : provinceOptions
                }
                value={filter.provinceId ? String(filter.provinceId) : String(currentUser?.province_id)}
                placeholder={t('select_province_id')}
                isDisplayAllOption
                isSearchEnable
                onChange={(e) =>
                  dispatch(locationActions.setFilter({ provinceId: e.target.value, pageIndex: PAGE_INDEX }))
                }
                disabled={isXaphuongTinhThanh(cast(currentUser?.roleid))}
              />
            </Box>
            <Box className='min-[1280px]:w-1/3 w-full'>
              <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('ward_id')}</Typography>
              <BaseSelect
                options={wardOptions}
                value={filter.wardId ? String(filter.wardId) : String(currentUser?.ward_id)}
                placeholder={t('select_ward_id')}
                isDisplayAllOption
                isSearchEnable
                onChange={(e) => dispatch(locationActions.setFilter({ wardId: e.target.value, pageIndex: PAGE_INDEX }))}
                disabled={isWard(cast(currentUser?.roleid))}
              />
            </Box>
            <Box className='min-[1280px]:w-1/3 w-full'>
              <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('location_type.title')}</Typography>
              <BaseSelect
                options={locationTypeOptions}
                value={filter.location_type ?? ''}
                placeholder={t('select_location_type')}
                isSearchEnable
                isDisplayAllOption
                onChange={(e) => {
                  const value = e.target.value;
                  dispatch(
                    locationActions.setFilter({
                      location_type: value === '' ? undefined : Number(value),
                      pageIndex: PAGE_INDEX,
                    })
                  );
                }}
              />
            </Box>
          </BaseRow>
          <BaseRow className='gap-4 max-[1280px]:!flex-col'>
            <Box className='w-full'>
              <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('is_assigned.title')}</Typography>
              <BaseSelect
                options={statusOptions}
                value={filter.assigned ?? ''}
                placeholder={t('select_is_assigned')}
                isSearchEnable
                isDisplayAllOption
                onChange={(e) => {
                  const value = e.target.value;
                  dispatch(
                    locationActions.setFilter({
                      assigned: value === '' ? undefined : Number(value),
                      pageIndex: PAGE_INDEX,
                    })
                  );
                }}
              />
            </Box>
            <Box className='w-full'>
              <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('createDate')}</Typography>
              <Box className='flex justify-between items-center gap-3 max-[768px]:!flex-col'>
                <Box className='w-full'>
                  <BaseDatePicker
                    portalId='datePicker-portalId'
                    value={filter.startDate}
                    dateFormat='YYYY-MM-DD'
                    onChange={(date) =>
                      dispatch(
                        locationActions.setFilter({
                          startDate: dayjs(date).startOf('day').format('YYYY-MM-DD'),
                          pageIndex: PAGE_INDEX,
                        })
                      )
                    }
                    placeholder={t('startDate')}
                    calendarProps={filter.endDate ? { maxDate: dayjs(filter.endDate).toDate() } : {}}
                    className='w-full'
                  />
                </Box>
                <Box className='w-4 h-px bg-black hidden lg:block'></Box>
                <Box className='w-full'>
                  <BaseDatePicker
                    portalId='datePicker-portalId'
                    value={filter.endDate}
                    dateFormat='YYYY-MM-DD'
                    onChange={(date) =>
                      dispatch(
                        locationActions.setFilter({
                          endDate: dayjs(date).endOf('day').format('YYYY-MM-DD'),
                          pageIndex: PAGE_INDEX,
                        })
                      )
                    }
                    placeholder={t('endDate')}
                    calendarProps={filter.startDate ? { minDate: dayjs(filter.startDate).toDate() } : {}}
                    className='w-full'
                  />
                </Box>
                <Box className='flex-1'>
                  <Button
                    className='border border-solid border-[#123B6D] bg-[#DCE8F5] rounded-xl text-base font-bold text-[#123B6D] w-[132px] hover:bg-[#0E2F58] hover:opacity-80 hover:text-white'
                    onClick={handleClearFilter}
                  >
                    {t('clear', { ns: 'admin/common' })}
                  </Button>
                </Box>
              </Box>
            </Box>
          </BaseRow>
        </Box>
      </BaseRow>

      <Box className='px-6'>
        <LocationList onDetail={handleOpenDetail} onOpenEdit={handleOpenEdit} onOpenDelete={handleOpenDelete} />
      </Box>

      {isOpenDetailLocationDialog && (
        <DetailLocationDialog
          data={editData}
          open={isOpenDetailLocationDialog}
          setOpen={setIsOpenDetailLocationDialog}
        />
      )}

      {isOpenEditLocationDialog && (
        <EditLocationDialog
          data={editData}
          currentUser={currentUser}
          open={isOpenEditLocationDialog}
          mode={editLocationDialogMode}
          setOpen={handleSetOpenEditDialog}
        />
      )}

      {isOpenDeleteLocationDialog && (
        <ConfirmDialog
          open={isOpenDeleteLocationDialog}
          title={t('delete')}
          description={t('confirm_delete', {
            name: editData?.name,
          })}
          onConfirm={handleDeleteLocation}
          onCancel={() => setIsOpenDeleteLocationDialog(false)}
        />
      )}
    </Box>
  );
};

export default memo(Location);
